import { Link } from "react-router-dom";
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import { buildManufacturingEditBatchPathForBatch } from "@/lib/manufacturingEditBatchPath";
import { formatNumber } from "@/lib/mrp";
import { formatTimestamp } from "@/lib/utils";

const PAGE_HELP =
  "Forward schedule for SGS and Suntronic through the latest open order ship-by date: batch sales orders, unassigned lines, Clamp QC work orders, and Assembly magnet kits compete for the same labor budget. Demand is scheduled day-by-day against TLA supply (including PO expected arrival) and station-level man-minute standards with WIP-aware remaining labor. Late risk is classified as TLA, labor, both, missing ETA, or on time.";

type LateCause =
  | "on_time"
  | "late_tla"
  | "late_labor"
  | "late_both"
  | "blocked_no_eta"
  | "unscheduled";

interface PlanAssignment {
  demandKey: string;
  day: string;
  locationId: number;
  units: number;
  manMinutes: number;
  tlaGateDate: string | null;
}

interface ScheduledLine {
  demandKey: string;
  locationId: number;
  batchId: number | null;
  salesOrderNumber: string;
  finishedSku: string;
  pendingUnits: number;
  shipByDate: string;
  source: "batch" | "unassigned" | "clamp_qc" | "assembly";
  plannedStartDate: string | null;
  plannedFinishDate: string | null;
  scheduledUnits: number;
  manMinutesPerUnit: number;
  totalRemainingManMinutes: number;
  lateCause: LateCause;
  assignments: PlanAssignment[];
}

const SOURCE_LABEL: Record<ScheduledLine["source"], string> = {
  batch: "Batch",
  unassigned: "Unassigned SO",
  clamp_qc: "Clamp QC",
  assembly: "Assembly",
};

interface DayUtilization {
  day: string;
  locationId: number;
  budgetManMinutes: number;
  scheduledManMinutes: number;
  utilizationPercent: number;
}

interface CapacityPlanResponse {
  startDate: string;
  endDate: string;
  locationIds: number[];
  timeZone: string;
  scheduledLines: ScheduledLine[];
  dayUtilization: DayUtilization[];
  tlaShortfalls: Array<{ sku: string; locationId: number; day: string; shortfallQuantity: number }>;
  unscheduledTlaInbound: Array<{ sku: string; locationId: number; quantity: number }>;
  configurationWarnings: string[];
}

const LATE_CAUSE_LABEL: Record<LateCause, string> = {
  on_time: "On time",
  late_tla: "Late — TLA",
  late_labor: "Late — labor",
  late_both: "Late — TLA and labor",
  blocked_no_eta: "Blocked — no TLA ETA",
  unscheduled: "Unscheduled in plan",
};

const LATE_CAUSE_CLASS: Record<LateCause, string> = {
  on_time: "bg-emerald-100 text-emerald-900 border-emerald-200",
  late_tla: "bg-amber-100 text-amber-950 border-amber-200",
  late_labor: "bg-orange-100 text-orange-950 border-orange-200",
  late_both: "bg-rose-100 text-rose-950 border-rose-200",
  blocked_no_eta: "bg-slate-200 text-slate-900 border-slate-300",
  unscheduled: "bg-violet-100 text-violet-950 border-violet-200",
};

function locationLabel(locationId: number): string {
  return locationId === 1 ? "SGS" : locationId === 2 ? "SUN" : `Site ${String(locationId)}`;
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${formatNumber(minutes)} min`;
  const hours = minutes / 60;
  return `${formatNumber(hours)} h`;
}

const CAPACITY_PLAN_QUERY_KEY = ["manufacturing", "production-capacity", "plan"] as const;

function fetchCapacityPlan(): Promise<CapacityPlanResponse> {
  return apiFetch<CapacityPlanResponse>("/api/manufacturing/production-capacity/plan/recompute", {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export function ProductionCapacityPage() {
  const queryClient = useQueryClient();

  const planQuery = useQuery({
    queryKey: CAPACITY_PLAN_QUERY_KEY,
    queryFn: () => fetchCapacityPlan(),
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 30 * 60 * 1000,
  });

  const recomputeMutation = useMutation({
    mutationFn: () => fetchCapacityPlan(),
    onSuccess: (data) => {
      queryClient.setQueryData(CAPACITY_PLAN_QUERY_KEY, data, {
        updatedAt: Date.now(),
      });
    },
  });

  const plan = planQuery.data;
  const lastRunLabel =
    plan != null && planQuery.dataUpdatedAt > 0
      ? formatTimestamp(new Date(planQuery.dataUpdatedAt).toISOString())
      : null;

  const utilizationByLocation = useMemo(() => {
    if (plan == null) return new Map<number, DayUtilization[]>();
    const map = new Map<number, DayUtilization[]>();
    for (const row of plan.dayUtilization) {
      const list = map.get(row.locationId) ?? [];
      list.push(row);
      map.set(row.locationId, list);
    }
    for (const list of map.values()) {
      list.sort((left, right) => left.day.localeCompare(right.day));
    }
    return map;
  }, [plan]);

  const linesByLocation = useMemo(() => {
    if (plan == null) return new Map<number, ScheduledLine[]>();
    const map = new Map<number, ScheduledLine[]>();
    for (const line of plan.scheduledLines) {
      const list = map.get(line.locationId) ?? [];
      list.push(line);
      map.set(line.locationId, list);
    }
    for (const list of map.values()) {
      list.sort((left, right) => {
        const byShip = left.shipByDate.localeCompare(right.shipByDate);
        if (byShip !== 0) return byShip;
        return left.demandKey.localeCompare(right.demandKey);
      });
    }
    return map;
  }, [plan]);

  const lateSummary = useMemo(() => {
    if (plan == null) return [];
    const counts = new Map<LateCause, number>();
    for (const line of plan.scheduledLines) {
      counts.set(line.lateCause, (counts.get(line.lateCause) ?? 0) + 1);
    }
    return [...counts.entries()].sort((left, right) => left[0].localeCompare(right[0]));
  }, [plan]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Production capacity</PageTitleWithHelp>
        <div className="flex flex-wrap items-end gap-3">
          <Button
            type="button"
            variant="secondary"
            className="h-auto min-h-10 flex-col gap-0 px-4 py-1.5"
            onClick={() => recomputeMutation.mutate()}
            disabled={recomputeMutation.isPending}
          >
            <span className="inline-flex items-center gap-2 leading-tight">
              <RefreshCw
                className={cn("h-4 w-4 shrink-0", recomputeMutation.isPending && "animate-spin")}
              />
              Recompute
            </span>
            {lastRunLabel != null ? (
              <span className="text-[11px] font-normal leading-tight text-slate-500 tabular-nums whitespace-nowrap">
                {lastRunLabel}
              </span>
            ) : recomputeMutation.isPending ? (
              <span className="text-[11px] font-normal leading-tight text-slate-500">Running…</span>
            ) : null}
          </Button>
        </div>
      </div>

      {plan?.configurationWarnings.length ? (
        <Card>
          <CardBody className="space-y-2 text-sm text-amber-900">
            {plan.configurationWarnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
            <p>Configure production capacity in Manufacturing Settings.</p>
          </CardBody>
        </Card>
      ) : null}

      {recomputeMutation.error ? (
        <p className="text-sm text-rose-700">{(recomputeMutation.error as Error).message}</p>
      ) : null}

      {plan == null && !recomputeMutation.isPending ? (
        <p className="text-sm text-slate-600">Click Recompute to build the schedule from open orders.</p>
      ) : null}

      {plan != null ? (
        <>
          <p className="text-sm text-slate-600">
            {formatNumber(plan.scheduledLines.length)} demand lines · {plan.startDate} through {plan.endDate}{" "}
            (weekdays)
          </p>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Late risk</CardTitle>
            </CardHeader>
            <CardBody className="space-y-1 text-sm">
              {lateSummary.length === 0 ? (
                <p className="text-slate-600">No open demand in plan.</p>
              ) : (
                lateSummary.map(([cause, count]) => (
                  <p key={cause}>
                    {LATE_CAUSE_LABEL[cause]}: {formatNumber(count)}
                  </p>
                ))
              )}
            </CardBody>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            {[1, 2].map((locationId) => (
              <Card key={locationId}>
                <CardHeader>
                  <CardTitle>{locationLabel(locationId)} labor utilization</CardTitle>
                </CardHeader>
                <CardBody className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-slate-600">
                        <th className="py-2 pr-3">Day</th>
                        <th className="py-2 pr-3 text-right">Budget</th>
                        <th className="py-2 pr-3 text-right">Scheduled</th>
                        <th className="py-2 text-right">Util %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(utilizationByLocation.get(locationId) ?? []).map((row) => (
                        <tr key={row.day} className="border-b border-slate-100">
                          <td className="py-2 pr-3 whitespace-nowrap">{row.day}</td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatMinutes(row.budgetManMinutes)}
                          </td>
                          <td className="py-2 pr-3 text-right tabular-nums">
                            {formatMinutes(row.scheduledManMinutes)}
                          </td>
                          <td
                            className={cn(
                              "py-2 text-right tabular-nums font-medium",
                              row.utilizationPercent > 100 ? "text-rose-700" : "text-slate-900",
                            )}
                          >
                            {formatNumber(row.utilizationPercent)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardBody>
              </Card>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {[1, 2].map((locationId) => (
              <Card key={`lanes-${String(locationId)}`}>
                <CardHeader>
                  <CardTitle>{locationLabel(locationId)} scheduled demand</CardTitle>
                </CardHeader>
                <CardBody className="space-y-3">
                  {(linesByLocation.get(locationId) ?? []).length === 0 ? (
                    <p className="text-sm text-slate-600">No open demand at this site.</p>
                  ) : (
                    (linesByLocation.get(locationId) ?? []).map((line) => (
                      <div
                        key={line.demandKey}
                        className={cn(
                          "rounded-md border px-3 py-2 text-sm",
                          LATE_CAUSE_CLASS[line.lateCause],
                        )}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="font-medium">
                              {line.salesOrderNumber} · {line.finishedSku}
                            </p>
                            <p className="text-xs opacity-80">
                              Ship by {line.shipByDate} · {formatNumber(line.pendingUnits)} units ·{" "}
                              {SOURCE_LABEL[line.source]}
                              {line.batchId != null ? (
                                <>
                                  {" "}
                                  ·{" "}
                                  <Link
                                    className="underline"
                                    to={buildManufacturingEditBatchPathForBatch(line.batchId)}
                                  >
                                    Batch {String(line.batchId)}
                                  </Link>
                                </>
                              ) : null}
                            </p>
                          </div>
                          <span className="rounded px-2 py-0.5 text-xs font-medium">
                            {LATE_CAUSE_LABEL[line.lateCause]}
                          </span>
                        </div>
                        <p className="mt-1 text-xs opacity-90">
                          Plan {line.plannedStartDate ?? "—"} → {line.plannedFinishDate ?? "—"} · scheduled{" "}
                          {formatNumber(line.scheduledUnits)} / {formatNumber(line.pendingUnits)} · remaining labor{" "}
                          {formatMinutes(line.totalRemainingManMinutes)} (
                          {formatNumber(line.manMinutesPerUnit)} min/unit)
                        </p>
                      </div>
                    ))
                  )}
                </CardBody>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>TLA inbound without ETA</CardTitle>
            </CardHeader>
            <CardBody>
              {plan.unscheduledTlaInbound.length === 0 ? (
                <p className="text-sm text-slate-600">All inbound TLA supply has an expected arrival date.</p>
              ) : (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="py-2 pr-4">Site</th>
                      <th className="py-2 pr-4">TLA SKU</th>
                      <th className="py-2 text-right">Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.unscheduledTlaInbound.map((row) => (
                      <tr key={`${String(row.locationId)}-${row.sku}`} className="border-b border-slate-100">
                        <td className="py-2 pr-4">{locationLabel(row.locationId)}</td>
                        <td className="py-2 pr-4 font-mono text-xs">{row.sku}</td>
                        <td className="py-2 text-right tabular-nums">{formatNumber(row.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardBody>
          </Card>

          {plan.tlaShortfalls.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>TLA shortfalls (plan end)</CardTitle>
              </CardHeader>
              <CardBody className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-600">
                      <th className="py-2 pr-4">Day</th>
                      <th className="py-2 pr-4">Site</th>
                      <th className="py-2 pr-4">TLA SKU</th>
                      <th className="py-2 text-right">Short</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.tlaShortfalls.map((row) => (
                      <tr
                        key={`${row.day}-${String(row.locationId)}-${row.sku}`}
                        className="border-b border-slate-100"
                      >
                        <td className="py-2 pr-4">{row.day}</td>
                        <td className="py-2 pr-4">{locationLabel(row.locationId)}</td>
                        <td className="py-2 pr-4 font-mono text-xs">{row.sku}</td>
                        <td className="py-2 text-right tabular-nums">
                          {formatNumber(row.shortfallQuantity)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
